import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  Chip,
  Stack,
  IconButton,
  Collapse,
} from '@mui/material';
import { LocalOffer, CheckCircle, Close, InfoOutlined } from '@mui/icons-material';
import { useApplyCoupon } from '../../../hooks/storefront/useApplyCoupon';
import { useRemoveCoupon } from '../../../hooks/storefront/useRemoveCoupon';
import { formatMoney } from '../../../utils/money';
import { useTranslation } from 'react-i18next';

const formatCouponValue = (coupon, currency) => {
  if (!coupon) return '';
  if (coupon.type === 'percent') return `${Number(coupon.value)}%`;
  return formatMoney(coupon.value, currency);
};

const CouponInput = ({ appliedCoupon, discount = 0, subtotal = 0, currency }) => {
  const { t } = useTranslation();
  const [couponCode, setCouponCode] = useState('');
  const applyCoupon = useApplyCoupon();
  const removeCoupon = useRemoveCoupon();

  const hasApplied = Boolean(appliedCoupon?.code);

  // Detect if discount was capped: backend returns applied discount which may be
  // less than the theoretical value from coupon type/value (max discount cap).
  const theoretical = (() => {
    if (!appliedCoupon) return 0;
    const value = Number(appliedCoupon.value) || 0;
    if (appliedCoupon.type === 'percent') return (Number(subtotal) || 0) * (value / 100);
    if (appliedCoupon.type === 'fixed' || appliedCoupon.type === 'amount') return value;
    return 0;
  })();
  const applied = Number(discount) || 0;
  // Use small epsilon to avoid float noise, and only flag when difference is meaningful (> 0.01 EGP)
  const isCapped = hasApplied && theoretical > 0 && theoretical - applied > 0.01;

  const handleApply = () => {
    const code = couponCode.trim();
    if (!code || applyCoupon.isPending) return;
    applyCoupon.mutate(code, {
      onSuccess: () => setCouponCode(''),
    });
  };

  const handleRemove = () => {
    if (removeCoupon.isPending) return;
    removeCoupon.mutate();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 2,
        border: '1px dashed',
        borderColor: hasApplied ? 'success.main' : 'divider',
        bgcolor: (theme) =>
          hasApplied
            ? theme.palette.mode === 'dark'
              ? 'rgba(46,125,50,0.08)'
              : 'success.lighter' || 'rgba(46,125,50,0.06)'
            : 'transparent',
        transition: 'border-color 0.2s, background-color 0.2s',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <LocalOffer color={hasApplied ? 'success' : 'action'} fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          {hasApplied
            ? t('cart.couponApplied', 'Coupon Applied')
            : t('cart.coupon', 'Coupon Code')}
        </Typography>
      </Stack>

      {hasApplied ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                icon={<CheckCircle fontSize="small" />}
                label={appliedCoupon.code}
                color="success"
                size="small"
                sx={{ fontWeight: 700, letterSpacing: 0.5 }}
              />
              <Typography variant="body2" color="text.secondary">
                {t('cart.youSaved', 'You saved')}{' '}
                <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
                  {formatMoney(applied, currency)}
                </Box>
                {appliedCoupon.value ? ` (${formatCouponValue(appliedCoupon, currency)})` : ''}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={handleRemove}
              disabled={removeCoupon.isPending}
              aria-label={t('cart.removeCoupon', 'Remove coupon')}
              sx={{
                color: 'error.main',
                '&:hover': { bgcolor: 'error.lighter' || 'rgba(211,47,47,0.08)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Max-discount cap hint */}
          <Collapse in={isCapped}>
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(2,136,209,0.12)'
                    : 'rgba(2,136,209,0.08)',
                border: '1px solid',
                borderColor: 'info.light',
              }}
            >
              <InfoOutlined fontSize="small" sx={{ color: 'info.main', mt: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'info.main' }}>
                  {t('cart.couponCapTitle', 'Maximum discount applied')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {t('cart.couponCapMessage', {
                    defaultValue:
                      'This coupon is {{value}} but the maximum discount allowed is {{max}}, so it was capped.',
                    value: formatCouponValue(appliedCoupon, currency),
                    max: formatMoney(applied, currency),
                  })}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('cart.couponPlaceholder', 'Enter your coupon code')}
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              onKeyDown={handleKeyPress}
              disabled={applyCoupon.isPending}
              error={Boolean(applyCoupon.error)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOffer color="action" fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              }}
            />
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={applyCoupon.isPending || !couponCode.trim()}
              sx={{
                minWidth: 110,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
              }}
            >
              {applyCoupon.isPending
                ? t('common.applying', 'Applying...')
                : t('common.apply', 'Apply')}
            </Button>
          </Box>
          <Collapse in={Boolean(applyCoupon.error)}>
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {applyCoupon.error?.response?.data?.message ||
                t('cart.couponError', 'Invalid coupon code')}
            </Typography>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

export default CouponInput;
